import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  getSalesClientEnrollmentsApi,
  getSalesRecruiterEnrollmentsApi,
  getSalesAppDownloadsApi,
  getSalesRecruiterClientRegistrationsApi,
  getSalesAnalyticsApi,
  getSalesFilingsApi,
  getSalesTopRecruitersApi,
} from '../../Api/AdminApiCall';

export const fetchSalesAnalytics = createAsyncThunk(
  'salesDashboard/fetchAnalytics',
  async (params, { rejectWithValue }) => {
    try {
      const response = await getSalesAnalyticsApi(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.response?.data || 'Failed to load sales analytics'
      );
    }
  }
);

export const fetchSalesFilings = createAsyncThunk(
  'salesDashboard/fetchFilings',
  async (params, { rejectWithValue }) => {
    try {
      const response = await getSalesFilingsApi(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.response?.data || 'Failed to load filings'
      );
    }
  }
);

export const fetchSalesTopRecruiters = createAsyncThunk(
  'salesDashboard/fetchTopRecruiters',
  async (params, { rejectWithValue }) => {
    try {
      const response = await getSalesTopRecruitersApi(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.response?.data || 'Failed to load top recruiters'
      );
    }
  }
);

export const fetchSalesRecruiterClientRegistrations = createAsyncThunk(
  'salesDashboard/fetchRecruiterClientRegistrations',
  async (params, { rejectWithValue }) => {
    try {
      const response = await getSalesRecruiterClientRegistrationsApi(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data ||
          'Failed to load recruiter client registrations'
      );
    }
  }
);

export const fetchSalesClientEnrollments = createAsyncThunk(
  'salesDashboard/fetchClientEnrollments',
  async (params, { rejectWithValue }) => {
    try {
      const response = await getSalesClientEnrollmentsApi(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data ||
          'Failed to load client enrollments'
      );
    }
  }
);

export const fetchSalesRecruiterEnrollments = createAsyncThunk(
  'salesDashboard/fetchRecruiterEnrollments',
  async (params, { rejectWithValue }) => {
    try {
      const response = await getSalesRecruiterEnrollmentsApi(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data ||
          'Failed to load recruiter enrollments'
      );
    }
  }
);

export const fetchSalesAppDownloads = createAsyncThunk(
  'salesDashboard/fetchAppDownloads',
  async (params, { rejectWithValue }) => {
    try {
      const response = await getSalesAppDownloadsApi(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data ||
          'Failed to load app downloads'
      );
    }
  }
);

const initialState = {
  analytics: {
    data: null,
    loading: false,
    error: null,
  },
  filings: {
    data: null,
    loading: false,
    error: null,
  },
  topRecruiters: {
    data: [],
    loading: false,
    error: null,
  },
  recruiterClientRegistrations: {
    data: [],
    pagination: {
      page: 1,
      limit: 20,
      total: 0,
      totalPages: 1,
    },
    loading: false,
    error: null,
  },
  clientEnrollments: {
    data: null,
    loading: false,
    error: null,
  },
  recruiterEnrollments: {
    data: null,
    loading: false,
    error: null,
  },
  appDownloads: {
    data: null,
    loading: false,
    error: null,
  },
};

const salesDashboardSlice = createSlice({
  name: 'salesDashboard',
  initialState,
  reducers: {
    clearSalesDashboardErrors: (state) => {
      state.analytics.error = null;
      state.filings.error = null;
      state.topRecruiters.error = null;
      state.recruiterClientRegistrations.error = null;
      state.clientEnrollments.error = null;
      state.recruiterEnrollments.error = null;
      state.appDownloads.error = null;
    },
  },
  extraReducers: (builder) => {
    // Analytics
    builder
      .addCase(fetchSalesAnalytics.pending, (state) => {
        state.analytics.loading = true;
        state.analytics.error = null;
      })
      .addCase(fetchSalesAnalytics.fulfilled, (state, action) => {
        state.analytics.loading = false;
        state.analytics.data = action.payload?.data || action.payload;
      })
      .addCase(fetchSalesAnalytics.rejected, (state, action) => {
        state.analytics.loading = false;
        state.analytics.error = action.payload;
      });

    // Filings
    builder
      .addCase(fetchSalesFilings.pending, (state) => {
        state.filings.loading = true;
        state.filings.error = null;
      })
      .addCase(fetchSalesFilings.fulfilled, (state, action) => {
        state.filings.loading = false;
        state.filings.data = action.payload?.data || action.payload;
      })
      .addCase(fetchSalesFilings.rejected, (state, action) => {
        state.filings.loading = false;
        state.filings.error = action.payload;
      });

    // Top Recruiters
    builder
      .addCase(fetchSalesTopRecruiters.pending, (state) => {
        state.topRecruiters.loading = true;
        state.topRecruiters.error = null;
      })
      .addCase(fetchSalesTopRecruiters.fulfilled, (state, action) => {
        state.topRecruiters.loading = false;
        state.topRecruiters.data = action.payload?.data || [];
      })
      .addCase(fetchSalesTopRecruiters.rejected, (state, action) => {
        state.topRecruiters.loading = false;
        state.topRecruiters.error = action.payload;
      });

    // Recruiter Client Registrations
    builder
      .addCase(fetchSalesRecruiterClientRegistrations.pending, (state) => {
        state.recruiterClientRegistrations.loading = true;
        state.recruiterClientRegistrations.error = null;
      })
      .addCase(fetchSalesRecruiterClientRegistrations.fulfilled, (state, action) => {
        state.recruiterClientRegistrations.loading = false;
        state.recruiterClientRegistrations.data = action.payload?.data || [];
        if (action.payload?.pagination) {
          state.recruiterClientRegistrations.pagination = action.payload.pagination;
        }
      })
      .addCase(fetchSalesRecruiterClientRegistrations.rejected, (state, action) => {
        state.recruiterClientRegistrations.loading = false;
        state.recruiterClientRegistrations.error = action.payload;
      });

    // Client Enrollments
    builder
      .addCase(fetchSalesClientEnrollments.pending, (state) => {
        state.clientEnrollments.loading = true;
        state.clientEnrollments.error = null;
      })
      .addCase(fetchSalesClientEnrollments.fulfilled, (state, action) => {
        state.clientEnrollments.loading = false;
        state.clientEnrollments.data = action.payload?.data || action.payload;
      })
      .addCase(fetchSalesClientEnrollments.rejected, (state, action) => {
        state.clientEnrollments.loading = false;
        state.clientEnrollments.error = action.payload;
      });

    // Recruiter Enrollments
    builder
      .addCase(fetchSalesRecruiterEnrollments.pending, (state) => {
        state.recruiterEnrollments.loading = true;
        state.recruiterEnrollments.error = null;
      })
      .addCase(fetchSalesRecruiterEnrollments.fulfilled, (state, action) => {
        state.recruiterEnrollments.loading = false;
        state.recruiterEnrollments.data = action.payload?.data || action.payload;
      })
      .addCase(fetchSalesRecruiterEnrollments.rejected, (state, action) => {
        state.recruiterEnrollments.loading = false;
        state.recruiterEnrollments.error = action.payload;
      });

    // App Downloads
    builder
      .addCase(fetchSalesAppDownloads.pending, (state) => {
        state.appDownloads.loading = true;
        state.appDownloads.error = null;
      })
      .addCase(fetchSalesAppDownloads.fulfilled, (state, action) => {
        state.appDownloads.loading = false;
        state.appDownloads.data = action.payload?.data || action.payload;
      })
      .addCase(fetchSalesAppDownloads.rejected, (state, action) => {
        state.appDownloads.loading = false;
        state.appDownloads.error = action.payload;
      });
  },
});

export const { clearSalesDashboardErrors } = salesDashboardSlice.actions;
export default salesDashboardSlice.reducer;
